/**
 * Segundo monitor: Document Picture-in-Picture (Chromium 116+), PiP clásico del <video> (Safari 18+)
 * y la ventana propia `/share/view/:id` (todos). Acá viven las dos primeras; la tercera es solo un
 * `window.open` con `noopener` desde el stage.
 *
 * Document PiP MUEVE el mismo <video> (el `srcObject` sigue, sin renegociar): el elemento no lo
 * maneja React sino el ShareProvider, así se puede re-parentar sin pelearse con el árbol.
 */
import { pipSize } from './peer';

type DocPipWindow = Window & { document: Document };
type DocPip = { requestWindow(opts?: { width?: number; height?: number; disallowReturnToOpener?: boolean }): Promise<DocPipWindow>; window: DocPipWindow | null };

export function docPipSupported(): boolean {
  return typeof window !== 'undefined' && 'documentPictureInPicture' in window;
}

/**
 * ¿Hay PiP clásico del <video>? Chromium y Safari (18+, con el workaround de `setVideoSource`) por
 * `requestPictureInPicture`/`webkitSetPresentationMode`; Firefox no expone la API hasta la 153.
 */
export function videoPipSupported(): boolean {
  if (typeof document === 'undefined' || typeof HTMLVideoElement === 'undefined') return false;
  const doc = document as Document & { pictureInPictureEnabled?: boolean };
  if (doc.pictureInPictureEnabled === true) return true;
  return 'webkitSetPresentationMode' in HTMLVideoElement.prototype;
}

export type WindowOption = 'openWindow' | 'popOut' | 'videoPip';

/**
 * Los botones de ventana del visor, en orden, según lo que el navegador puede (spec §4.6):
 * "Open in a window" SIEMPRE y primero en los cinco navegadores; "Pop out" (Document PiP) solo si
 * existe `documentPictureInPicture`; PiP clásico del <video> como tercera opción. El inspector
 * (teléfono) y la ventana propia no tienen ninguno. Pura: se prueba en vitest/node.
 */
export function windowOptions(caps: { docPip: boolean; videoPip: boolean; field: boolean; windowMode: boolean }): WindowOption[] {
  if (caps.field || caps.windowMode) return [];
  const out: WindowOption[] = ['openWindow'];
  if (caps.docPip) out.push('popOut');
  if (caps.videoPip) out.push('videoPip');
  return out;
}

function docPip(): DocPip | null {
  return (window as unknown as { documentPictureInPicture?: DocPip }).documentPictureInPicture ?? null;
}

/** Copia los estilos a mano (Chrome quitó `copyStyleSheets`): las hojas propias como texto, las ajenas como <link>. */
export function copyStyles(into: Document): void {
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      const css = Array.from(sheet.cssRules).map((r) => r.cssText).join('\n');
      const el = into.createElement('style');
      el.textContent = css;
      into.head.appendChild(el);
    } catch {
      if (sheet.href) {
        const link = into.createElement('link');
        link.rel = 'stylesheet';
        link.href = sheet.href;
        into.head.appendChild(link);
      }
    }
  }
  const theme = document.documentElement.getAttribute('data-theme');
  if (theme) into.documentElement.setAttribute('data-theme', theme);
}

export type DocPipHandle = {
  win: DocPipWindow;
  /** Tamaño real con el que abrió (Chrome recorta al 25 % del área): va a stats_json. */
  size: { width: number; height: number };
  close(): void;
};

/**
 * Abre la ventana PiP con el 25 % del área en 16:9, mueve `video` adentro con una barra mínima
 * (Enlarge + pista) y avisa `onReturn` cuando la ventana se cierra para que el stage lo recupere.
 * Requiere gesto del usuario: llamar desde el click.
 */
export async function openDocPip(
  video: HTMLVideoElement,
  labels: { enlarge: string; dragCorner: string; title: string },
  onReturn: () => void,
): Promise<DocPipHandle | null> {
  const api = docPip();
  if (!api) return null;
  const { width, height } = pipSize(screen.width, screen.height);
  let win: DocPipWindow;
  try { win = await api.requestWindow({ width, height }); }
  catch { return null; }
  copyStyles(win.document);
  win.document.title = labels.title;
  const root = win.document.createElement('div');
  root.className = 'ss-pip-root';
  const bar = win.document.createElement('div');
  bar.className = 'ss-pip-bar';
  const title = win.document.createElement('span');
  title.className = 'ss-pip-title';
  title.textContent = labels.title;
  const hint = win.document.createElement('span');
  hint.className = 'ss-pip-hint';
  hint.textContent = labels.dragCorner;
  const enlarge = win.document.createElement('button');
  enlarge.className = 'btn ghost sm';
  enlarge.type = 'button';
  enlarge.textContent = labels.enlarge;
  // resizeTo exige gesto DENTRO de la ventana PiP y se clampa al 80 % (Chrome 121+).
  enlarge.addEventListener('click', () => {
    try { win.resizeTo(Math.floor(screen.availWidth * 0.8), Math.floor(screen.availHeight * 0.8)); } catch { /* sin permiso */ }
  });
  bar.append(title, hint, enlarge);
  root.append(bar, video);
  win.document.body.appendChild(root);
  win.document.body.className = 'ss-pip-body';
  let returned = false;
  const back = () => { if (returned) return; returned = true; onReturn(); };
  win.addEventListener('pagehide', back);
  return {
    win,
    size: { width: win.innerWidth, height: win.innerHeight },
    close() { try { win.close(); } catch { /* */ } back(); },
  };
}

/**
 * PiP clásico del <video> (Safari 18+, también Chromium). El workaround del bug 262479 de WebKit
 * (controls=true → srcObject → controls=false) se aplica al asignar el srcObject; acá solo se pide
 * y se COMPRUEBA: si el navegador dijo que sí pero no abrió nada, se devuelve false sin error.
 */
export async function requestVideoPip(video: HTMLVideoElement): Promise<boolean> {
  const v = video as HTMLVideoElement & { requestPictureInPicture?: () => Promise<unknown>; webkitSetPresentationMode?: (m: string) => void };
  try {
    if (typeof v.requestPictureInPicture === 'function') {
      await v.requestPictureInPicture();
      await new Promise((r) => setTimeout(r, 150));
      return document.pictureInPictureElement === video;
    }
    if (typeof v.webkitSetPresentationMode === 'function') {
      v.webkitSetPresentationMode('picture-in-picture');
      await new Promise((r) => setTimeout(r, 150));
      return (v as unknown as { webkitPresentationMode?: string }).webkitPresentationMode === 'picture-in-picture';
    }
  } catch { /* cae a la ventana propia */ }
  return false;
}

export async function exitVideoPip(): Promise<void> {
  try { if (document.pictureInPictureElement) await document.exitPictureInPicture(); } catch { /* */ }
}

/** Asigna el srcObject con el workaround de Safari 17/18 para el PiP clásico. */
export function setVideoSource(video: HTMLVideoElement, stream: MediaStream | null): void {
  video.controls = true;
  video.srcObject = stream;
  video.controls = false;
}
